import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Play } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { currentWarehouseUser } from "@/lib/warehouseSession";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function StockCountNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [scopeType, setScopeType] = useState("full");
  const [scopeValue, setScopeValue] = useState("");
  const [blind, setBlind] = useState(true);
  const [options, setOptions] = useState({ categories: [], suppliers: [], brands: [], shelves: [], locations: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/stock-count/scopes/options`).then((r) => setOptions(r.data || options)).catch(() => {});
    // Default the session name to something friendly.
    setName(`Count ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`);
  }, []);

  const scopeChoices = () => {
    if (scopeType === "category") return options.categories;
    if (scopeType === "supplier") return options.suppliers;
    if (scopeType === "brand") return options.brands;
    return [];
  };

  const create = async () => {
    if (!name.trim()) { toast.error("Give the session a name"); return; }
    if (scopeType !== "full" && !scopeValue) { toast.error(`Pick a ${scopeType}`); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        scope: { type: scopeType, value: scopeType === "full" ? null : scopeValue },
        blind,
        created_by: currentWarehouseUser() || user?.name || user?.username || null,
      };
      const res = await axios.post(`${API}/stock-count/sessions`, payload);
      toast.success(`Session ${res.data.code} started`);
      navigate(`/stock-count/session/${res.data.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create session");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto p-4 space-y-4">
      <Link to="/stock-count" className="text-sm text-slate-500 flex items-center gap-1 hover:text-slate-900">
        <ArrowLeft className="h-3 w-3" /> Back to sessions
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>New Stock Count</CardTitle>
          <CardDescription>
            Pair a PC (USB scanner) and phones (cameras) using the code you&apos;ll get on the next screen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Session name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="name-input" />
          </div>

          <div>
            <Label>Scope</Label>
            <Select value={scopeType} onValueChange={(v) => { setScopeType(v); setScopeValue(""); }}>
              <SelectTrigger data-testid="scope-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full store (everything)</SelectItem>
                <SelectItem value="category">By category</SelectItem>
                <SelectItem value="supplier">By supplier</SelectItem>
                <SelectItem value="location">By shelf / bin (location)</SelectItem>
                <SelectItem value="brand">By brand</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scopeType !== "full" && scopeType !== "location" && (
            <div>
              <Label>{scopeType[0].toUpperCase() + scopeType.slice(1)}</Label>
              <Select value={scopeValue} onValueChange={setScopeValue}>
                <SelectTrigger data-testid="scope-value-select"><SelectValue placeholder={`Pick a ${scopeType}`} /></SelectTrigger>
                <SelectContent>
                  {scopeChoices().length === 0
                    ? <SelectItem value="__none__" disabled>Nothing set yet</SelectItem>
                    : scopeChoices().map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {scopeType === "location" && (
            <div>
              <Label>Shelf or bin</Label>
              <Select value={scopeValue} onValueChange={setScopeValue}>
                <SelectTrigger data-testid="scope-location-select">
                  <SelectValue placeholder="Pick a shelf or a specific bin" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(options.shelves?.length || 0) > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500 sticky top-0 bg-white">
                        Whole shelves ({options.shelves.length})
                      </div>
                      {options.shelves.map((s) => (
                        <SelectItem key={`shelf-${s}`} value={s}>
                          {s} <span className="text-xs text-slate-500 ml-1">(all bins)</span>
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {(options.locations?.length || 0) > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500 sticky top-0 bg-white border-t">
                        Specific bins ({options.locations.length})
                      </div>
                      {options.locations.map((loc) => (
                        <SelectItem key={`bin-${loc}`} value={loc}>{loc}</SelectItem>
                      ))}
                    </>
                  )}
                  {(options.shelves?.length || 0) === 0 && (options.locations?.length || 0) === 0 && (
                    <SelectItem value="__none__" disabled>No locations set on any product yet</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Pick a whole shelf (counts every bin under it) or a specific bin.
              </p>
            </div>
          )}

          <div>
            <Label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={blind}
                onChange={(e) => setBlind(e.target.checked)}
                className="rounded"
                data-testid="blind-checkbox"
              />
              Blind count (hide system stock from counter)
            </Label>
            <p className="text-xs text-slate-500 mt-1 ml-6">
              Recommended for auditing. Uncheck if you want counters to see the system qty next to the count.
            </p>
          </div>

          {scopeType === "location" && !!scopeValue && (
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900">
              Any product you scan on this session whose Stock Master location is not
              <span className="font-mono px-1 bg-blue-100 rounded ml-1">{scopeValue}</span>
              will be automatically reassigned to this shelf/bin — physical shelf becomes the source of truth.
            </div>
          )}

          <div className="pt-2 border-t">
            <Button onClick={create} disabled={saving} className="w-full" data-testid="create-session-btn">
              <Play className="h-4 w-4 mr-2" />
              {saving ? "Creating…" : "Start counting"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
